import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Modal, StatusHint, Steps } from '@indipia/ui-core';
import { createSite, fetchAgents, updateSiteAccess } from './api';
import { StepA } from './StepA';
import { StepB } from './StepB';
import { StepC } from './StepC';
import { StepD } from './StepD';
import { StepMap } from './StepMap';
import type { CreateSitePayload, PendingSiteInput, StepAResult, StepBResult, SiteRegistrationPopupProps } from './types';

/** A·B 이후에 붙을 수 있는 추가 단계 — requiredSteps 콤마 리스트에 담기는 값만 인정 */
type OptionalStepKey = 'C' | 'D';
const OPTIONAL_STEP_LABELS: Record<OptionalStepKey, string> = { C: '도면', D: '공사기간' };

/**
 * 현장 등록 3~N단계 팝업
 *
 * Step A — 고객 정보 (고객명 + 전화번호, 신규/기존 구분)
 * Step B — 현장 정보 (도로명 주소 검색 → 좌표·건물 자동 조회 → createSite payload 조립)
 * Step M — 지도 (핀으로 동 위치 확정 → 확정 좌표로 저장). mapStep=true(기본)일 때만.
 * Step C — 도면 등록 (기존 도면 선택 또는 이미지 업로드 → Vulcan 변환 → 저장).
 * Step D — 에이전트 단 공사기간 (스캐폴드, 항목 미정).
 *   C·D는 requiredSteps(콤마 리스트, 예: "C" | "D" | "C,D")에 담긴 순서대로만 노출된다.
 *
 * 저장(createSite)은 팝업이 담당한다.
 * - mapStep=true: Step B에서 조립한 payload를 보관 → 지도 확정 좌표(gpsLat/gpsLng)로 덮어써 저장
 * - mapStep=false: Step B payload(자동 지오코딩 좌표)로 즉시 저장
 */
export function SiteRegistrationPopup({ agentId, onClose, onComplete, mapStep = true, prefill }: SiteRegistrationPopupProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepAResult, setStepAResult] = useState<StepAResult | null>(null);
  const [stepBResult, setStepBResult] = useState<StepBResult | null>(null);
  const [pending, setPending] = useState<PendingSiteInput | null>(null);
  // Step A에서 기존 현장을 골라 들어왔는지 — [이전] 목적지 결정에 사용
  const [fromExistingSite, setFromExistingSite] = useState(false);

  const { data: agents = [] } = useQuery({
    queryKey: ['agents'],
    queryFn: fetchAgents,
    staleTime: 5 * 60_000,
  });

  const agent = agents.find((a) => a.agentId === agentId) ?? null;
  // A·B 뒤에 붙는 추가 단계 순서 — requiredSteps 콤마 리스트 파싱 (정의서 E-3)
  const optionalSteps: OptionalStepKey[] = (agent?.requiredSteps ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is OptionalStepKey => s === 'C' || s === 'D');
  const requiresStepC = optionalSteps.includes('C');
  // 평형 필수 여부 — 인디플래너(agentId=8/E01) 전용 (정의서 E-4)
  const isPyeongRequired = agentId === 8;

  // 동적 단계 구성 — A · B · [지도] · [optionalSteps 순서대로]
  const STEPS = ['고객', '현장'];
  if (mapStep) STEPS.push('지도');
  for (const key of optionalSteps) STEPS.push(OPTIONAL_STEP_LABELS[key]);
  const mapStepIndex = mapStep ? 2 : -1;
  // optionalSteps가 시작되는 위치 — 여기서부터 optionalSteps[0], [1] ... 순서로 이어짐
  const optionalBaseIndex = mapStep ? 3 : 2;
  const stepCIndex = requiresStepC ? optionalBaseIndex + optionalSteps.indexOf('C') : -1;
  const stepDIndex = optionalSteps.includes('D') ? optionalBaseIndex + optionalSteps.indexOf('D') : -1;

  /** C/D 단계 완료 시 호출 — 다음 optional 단계가 있으면 그리로, 없으면 전체 완료 처리 */
  function handleOptionalStepComplete(
    siteId: number,
    fromKey: OptionalStepKey,
    opts?: { selectedExisting?: boolean },
  ) {
    const idx = optionalSteps.indexOf(fromKey);
    const nextKey = optionalSteps[idx + 1];
    if (nextKey) {
      setCurrentStep(optionalBaseIndex + idx + 1);
    } else {
      handleComplete(siteId, opts?.selectedExisting ?? false);
    }
  }

  const updateSiteAccessMutation = useMutation({ mutationFn: updateSiteAccess });

  const createSiteMutation = useMutation({
    mutationFn: createSite,
    onSuccess: ({ siteId }, payload) => {
      // LAST_ACCESS_DTM 갱신 (비동기, non-blocking)
      updateSiteAccessMutation.mutate(siteId);
      if (optionalSteps.length > 0) {
        setStepBResult({
          siteId,
          bldgId: payload.bldgId ?? null,
          roadAddress: payload.siteAddress,
          sitePyeong: payload.sitePyeong ?? null,
          squareMeters: payload.squareMeters ?? null,
          bldgName: payload.bldgName ?? null,
          addressDong: payload.addressDong ?? null,
          addressHo: payload.addressHo ?? null,
        });
        setCurrentStep(optionalBaseIndex);
      } else {
        handleComplete(siteId);
      }
    },
    onError: (e) => {
      // 방어용 — 화면 검증을 통과했는데 서버가 관리자 알림 수신 여부 누락으로 거부한 경우(14-Customer SITE_MANAGER_NOTI_REQUIRED)
      if ((e as { response?: { data?: { code?: string } } })?.response?.data?.code === 'SITE_MANAGER_NOTI_REQUIRED') {
        toast.error('관리자 알림 받기를 켬 또는 끔으로 선택해주세요.');
        return;
      }
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '현장 등록에 실패했습니다.';
      toast.error(msg);
    },
  });

  /**
   * Step A에서 기존 현장을 선택 — 추가 단계(C·D)가 있으면 그 단계를 그대로 밟는다.
   * 부족한 현장 정보는 각 단계가 siteId로 직접 조회해 채우므로 여기서는 siteId만 넘긴다.
   */
  function handleSelectExistingSite(siteId: number) {
    if (optionalSteps.length === 0) {
      toast.success('현장이 선택되었습니다.');
      onComplete?.(siteId);
      onClose();
      return;
    }
    setFromExistingSite(true);
    setStepBResult({
      siteId,
      bldgId: null,
      roadAddress: '',
      sitePyeong: null,
      squareMeters: null,
      bldgName: null,
      addressDong: null,
      addressHo: null,
    });
    setCurrentStep(optionalBaseIndex);
  }

  function handleStepANext(result: StepAResult) {
    setFromExistingSite(false);
    setStepAResult(result);
    setCurrentStep(1);
  }

  // Step B 완료 — mapStep이면 지도 단계로, 아니면 즉시 저장
  function handleStepBSubmit(payload: CreateSitePayload) {
    if (mapStep) {
      setPending({
        payload,
        roadAddress: payload.siteAddress,
        initialLat: payload.gpsLat,
        initialLng: payload.gpsLng,
      });
      setCurrentStep(mapStepIndex);
    } else {
      createSiteMutation.mutate(payload);
    }
  }

  // 지도 확정 — 확정 좌표로 gps를 덮어써 저장
  function handleMapConfirm(coord: { lat: number; lng: number }) {
    if (!pending) return;
    createSiteMutation.mutate({ ...pending.payload, gpsLat: coord.lat, gpsLng: coord.lng });
  }

  function handleComplete(siteId: number, selectedExisting = false) {
    // 기등록 현장을 '확인'으로 고른 경우만 '선택' — 저장(신규 등록/공사현황 생성)은 '등록 완료'
    toast.success(selectedExisting ? '현장이 선택되었습니다.' : '현장 등록이 완료되었습니다.');
    onComplete?.(siteId);
    onClose();
  }

  // 각 단계에서 무엇을 하는지 — 단계명은 Steps 가 이미 보여주므로 문맥(고객명)만 남긴다
  const stepHint =
    currentStep === mapStepIndex ? '지도에서 동 위치를 지정해주세요'
    : stepAResult ? `${stepAResult.customerName} 고객`
    : null;

  return (
    // 딤·포커스 트랩·Esc·scrim 클릭·포커스 복원은 공용 Modal 이 갖는다 (D-09 §15.6)
    <Modal open onClose={onClose} title="현장 등록" size="lg">
      {/* 단계 표시 — 정렬·상하 여백은 .steps-wrap 이 담당한다 (D-09 §15.22) */}
      <Steps steps={STEPS} current={currentStep} />

      {stepHint && (
        <div className="mb-3 flex justify-center">
          <StatusHint tone="info">{stepHint}</StatusHint>
        </div>
      )}

      {/* 단계별 콘텐츠 — .modal-body 는 스크롤 컨테이너가 아니라 여기서 높이를 제한한다 */}
      <div className="max-h-[60vh] overflow-y-auto">
        {currentStep === 0 && (
          <StepA
            onNext={handleStepANext}
            onSelectExistingSite={handleSelectExistingSite}
            prefill={prefill && { customerName: prefill.customerName, customerMobile: prefill.customerMobile }}
          />
        )}

        {currentStep === 1 && stepAResult && (
          <StepB
            stepAResult={stepAResult}
            isPyeongRequired={isPyeongRequired}
            agentId={agentId}
            onSubmit={handleStepBSubmit}
            onPrev={() => setCurrentStep(0)}
            submitting={createSiteMutation.isPending}
            nextLabel={mapStep ? '지도에서 위치 지정 →' : '다음 →'}
            prefill={prefill && { siteName: prefill.siteName, siteAddress: prefill.siteAddress, sitePyeong: prefill.sitePyeong }}
          />
        )}

        {mapStep && currentStep === mapStepIndex && pending && (
          <StepMap
            roadAddress={pending.roadAddress}
            initialLat={pending.initialLat}
            initialLng={pending.initialLng}
            onPrev={() => setCurrentStep(1)}
            onConfirm={handleMapConfirm}
            confirmLabel={optionalSteps.length > 0 ? '이 위치로 확정 →' : '이 위치로 저장'}
            submitting={createSiteMutation.isPending}
          />
        )}

        {requiresStepC && currentStep === stepCIndex && stepBResult && (
          <StepC
            stepBResult={stepBResult}
            onPrev={() => setCurrentStep(fromExistingSite ? 0 : 1)}
            onComplete={(siteId) => handleOptionalStepComplete(siteId, 'C')}
            onClose={onClose}
          />
        )}

        {stepDIndex >= 0 && currentStep === stepDIndex && stepBResult && (
          <StepD
            stepBResult={stepBResult}
            onPrev={() => setCurrentStep(fromExistingSite ? 0 : stepCIndex >= 0 ? stepCIndex : 1)}
            onComplete={(siteId, opts) => handleOptionalStepComplete(siteId, 'D', opts)}
            onClose={onClose}
          />
        )}
      </div>
    </Modal>
  );
}
